import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import type { MarketType, RunDetail, StrategySpec, Trade } from "@strat/format";
import { ReportPdfButton, backtestReport } from "@strat/ui/export";

import { api } from "../../api";
import { ExportMenu } from "@strat/ui";
import { PagedList } from "../../components/PagedList";
import { CopySettingsButton } from "../../components/CopySettings";
import { EquityChart } from "../../components/EquityChart";
import { RunCharts } from "../../components/RunCharts";
import { RefChip } from "../../components/RefChip";
import { SortableTable } from "../../components/SortableTable";
import { SpecSummary } from "../../components/SpecSummary";
import {
  AuthorChip,
  CommentsPanel,
  Disclaimer,
  SaveButton,
  RatingPanel,
  Stars,
} from "../../components/social";
import { date, dd, money, num, pct, timeAgo, tone } from "../../format";
import { useSession } from "../../session";
import { Alert, Card, EmptyState, Field, Stat } from "../../shell/ui";
import { Icon } from "../../components/Icon";

interface RunResponse {
  run: {
    id: string;
    title: string | null;
    description: string;
    strategy_id: string | null;
    strategy_name: string;
    symbol: string;
    timeframe: string;
    market_type: string;
    visibility: "private" | "public";
    spec: StrategySpec;
    spec_hash: string;
    params: { initialCapital: number; from: number | null; to: number | null };
    stats: {
      totalReturnPct: number;
      maxDrawdownPct: number;
      winRatePct: number;
      totalTrades: number;
      profitFactor: number | null;
      finalEquity: number;
      exposurePct: number;
    };
    engine_version: string;
    engine_semantics: number;
    rating_avg: number;
    rating_count: number;
    verify_count: number;
    verify_ok_count: number;
    published_at: string | null;
    created_at: string;
    sharpe: number | null;
    author_username: string;
    author_name: string;
    author_avatar: string;
    author_simulated: boolean;
    author_badge: "verified" | null;
  };
  equity: Array<[number, number]>;
  trades: Trade[];
  tradeCount: number;
  analytics: { sharpeRatio?: number; buyHoldReturnPct?: number; cagrPct?: number } | null;
  barsTested: number;
  verifications: Array<{
    matched: boolean;
    created_at: string;
    username: string | null;
    display_name: string | null;
  }>;
  isOwner: boolean;
  strategyRef: {
    namespace: string;
    slug: string;
    version: number | null;
    digest: string;
    ref: string;
  } | null;
}

interface VerifyOutcome {
  matched: boolean;
  storedTrades: number;
  freshTrades: number;
  storedReturnPct: number;
  freshReturnPct: number;
  firstDivergence: { index: number } | null;
  engineVersion: string;
}

function PublishRun({ run }: { run: RunResponse["run"] }) {
  const qc = useQueryClient();
  const [title, setTitle] = useState(run.title ?? `${run.strategy_name} on ${run.symbol} ${run.timeframe}`);
  const [description, setDescription] = useState(run.description);
  const publish = useMutation({
    mutationFn: () => api(`/hub/runs/${run.id}/publish`, { method: "POST", body: { title, description } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["hub-run", run.id] }),
  });
  const unpublish = useMutation({
    mutationFn: () => api(`/hub/runs/${run.id}/unpublish`, { method: "POST" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["hub-run", run.id] }),
  });
  return (
    <Card title={run.visibility === "public" ? "Published benchmark" : "Publish as a benchmark card"}>
      <div className="stack">
        <p className="muted small">
          A benchmark card is public and reviewable, and anyone can re-run it to check it matches.
        </p>
        <Field label="Title">
          <input value={title} maxLength={140} onChange={(e) => setTitle(e.target.value)} />
        </Field>
        <Field label="Notes">
          <textarea
            rows={3}
            value={description}
            maxLength={4000}
            onChange={(e) => setDescription(e.target.value)}
          />
        </Field>
        {publish.error && <Alert tone="error">{publish.error.message}</Alert>}
        <div className="row">
          <button
            type="button"
            className="btn btn--primary"
            disabled={publish.isPending}
            onClick={() => publish.mutate()}
          >
            {run.visibility === "public" ? "Update" : "Publish"}
          </button>
          {run.visibility === "public" && (
            <button type="button" className="btn" onClick={() => unpublish.mutate()}>
              Make private
            </button>
          )}
        </div>
      </div>
    </Card>
  );
}

export function RunPage() {
  const { id = "" } = useParams();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { user } = useSession();
  const q = useQuery({
    queryKey: ["hub-run", id],
    queryFn: () => api<RunResponse>(`/hub/runs/${encodeURIComponent(id)}`),
    retry: false,
  });
  const verify = useMutation({
    mutationFn: () => api<VerifyOutcome>(`/hub/runs/${id}/verify`, { method: "POST", body: {} }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["hub-run", id] }),
  });
  if (q.isLoading) return null;
  if (!q.data) {
    return (
      <Card>
        <EmptyState icon="▦" title="Benchmark not found">
          It may be private, or no longer exists.
        </EmptyState>
      </Card>
    );
  }
  const { run: r } = q.data;
  const inv = [["hub-run", id]];
  const v = verify.data;
  return (
    <div className="stack detail">
      <header className="detail__head">
        <div>
          <p className="muted small">
            Benchmark · {r.symbol} {r.timeframe} · {r.market_type}
          </p>
          <h1 className="page__title">{r.title ?? `${r.strategy_name} on ${r.symbol} ${r.timeframe}`}</h1>
          <p className="detail__meta">
            <AuthorChip
              username={r.author_username}
              name={r.author_name}
              avatar={r.author_avatar}
              simulated={r.author_simulated}
              badge={r.author_badge}
            />
            <span className="muted small">run {timeAgo(r.created_at)}</span>
            <Stars value={r.rating_avg} count={r.rating_count} />
          </p>
          {r.strategy_id && (
            <p className="small">
              Strategy: <Link to={`/s/${r.strategy_id}`}>{r.strategy_name}</Link>
              {q.data.strategyRef && (
                <>
                  {" "}
                  <RefChip
                    namespace={q.data.strategyRef.namespace}
                    slug={q.data.strategyRef.slug}
                    version={q.data.strategyRef.version}
                    digest={q.data.strategyRef.digest}
                  />
                </>
              )}
            </p>
          )}
        </div>
        <div className="detail__side">
          <div className="row detail__actions">
            {user ? (
              <button
                type="button"
                className="btn btn--primary"
                onClick={() => navigate(`/workbench?runId=${r.id}`)}
                title="Load these exact settings in the Workbench"
              >
                <Icon name="workbench" /> Open in Workbench
              </button>
            ) : (
              <Link
                className="btn btn--primary"
                to={`/signin?next=${encodeURIComponent(`/workbench?runId=${r.id}`)}`}
              >
                <Icon name="workbench" /> Open in Workbench
              </Link>
            )}
            <CopySettingsButton
              settings={{
                strategy: r.spec,
                symbol: r.symbol,
                timeframe: r.timeframe,
                marketType: r.market_type,
                from: r.params.from ?? q.data.equity[0]?.[0] ?? null,
                to: r.params.to ?? q.data.equity.at(-1)?.[0] ?? null,
                initialCapital: r.params.initialCapital,
              }}
            />
            <SaveButton id={r.id} />
            <ReportPdfButton
              size="md"
              build={async () => {
                // The full result — the page itself carries a thinned copy. RunCharts reads the same query.
                const d = await qc.fetchQuery({
                  queryKey: ["hub-run-detail", r.id],
                  queryFn: () => api<RunDetail>(`/backtest/runs/${r.id}`),
                  staleTime: Infinity,
                });
                return backtestReport({
                  result: d.result,
                  title: r.title,
                  strategyName: r.strategy_name,
                  symbol: r.symbol,
                  timeframe: r.timeframe,
                  marketType: r.market_type,
                  initialCapital: r.params.initialCapital,
                  spec: r.spec,
                });
              }}
            />
            {user && (
              <ExportMenu
                size="md"
                request={{ runIds: [r.id] }}
                strategy={{
                  spec: r.spec,
                  settings: {
                    ...r.params,
                    symbol: r.symbol,
                    timeframe: r.timeframe,
                    marketType: r.market_type as MarketType,
                  },
                }}
              />
            )}
          </div>
          <p className="muted small action-hint">
            Opens with the same rules, market, window and capital — change anything and run it again.{" "}
            {q.data.isOwner ? "" : "Saving keeps a copy in your library."}
          </p>
        </div>
      </header>
      {r.description && <p className="detail__desc">{r.description}</p>}

      <Card title="Check the numbers yourself">
        <div className="verify-box">
          <div>
            <p>
              Re-run this exact strategy (spec <code title={r.spec_hash}>{r.spec_hash.slice(0, 12)}…</code>)
              on the same market and window, on this hub's engine, and compare every trade.
            </p>
            <p className="muted small">
              Checked {r.verify_count} time{r.verify_count === 1 ? "" : "s"} · {r.verify_ok_count} matched
            </p>
          </div>
          <button
            type="button"
            className="btn btn--primary"
            disabled={verify.isPending}
            onClick={() => verify.mutate()}
          >
            {verify.isPending ? "Re-running…" : "↻ Re-run to verify"}
          </button>
        </div>
        {verify.error && <Alert tone="error">{verify.error.message}</Alert>}
        {v && (
          <div className={`verdict ${v.matched ? "verdict--good" : "verdict--bad"}`} role="status">
            <span className="verdict__mark">{v.matched ? "✓" : "✗"}</span>
            <div>
              <strong className="verdict__headline">
                {v.matched
                  ? "Identical — every trade matches."
                  : `Differs from trade ${(v.firstDivergence?.index ?? 0) + 1}.`}
              </strong>
              <p className="verdict__detail small">
                {v.storedTrades} stored trades, {v.freshTrades} re-run · return {pct(v.storedReturnPct, 2)} vs{" "}
                {pct(v.freshReturnPct, 2)} · engine {v.engineVersion}
              </p>
            </div>
          </div>
        )}
      </Card>

      <div className="detail__grid">
        <div className="stack">
          <Card title="Result">
            <div className="stat-grid stat-grid--pairs">
              <Stat
                label="Return"
                value={<span className={tone(r.stats.totalReturnPct)}>{pct(r.stats.totalReturnPct)}</span>}
              />
              <Stat label="Buy & hold" value={pct(q.data.analytics?.buyHoldReturnPct)} />
              <Stat label="Max drawdown" value={dd(r.stats.maxDrawdownPct, 1)} />
              <Stat label="Win rate" value={pct(r.stats.winRatePct, 0, false)} />
              <Stat label="Trades" value={r.stats.totalTrades} />
              <Stat label="Sharpe" value={num(q.data.analytics?.sharpeRatio ?? r.sharpe)} />
              <Stat label="Exposure" value={pct(r.stats.exposurePct, 0, false)} />
              <Stat label="Final equity" value={money(r.stats.finalEquity)} />
            </div>
            <EquityChart points={q.data.equity} baseline={r.params.initialCapital} label="Backtest equity" />
            <p className="muted small mono">
              {q.data.barsTested} bars · {date(q.data.equity[0]?.[0])} → {date(q.data.equity.at(-1)?.[0])} ·
              engine {r.engine_version} · semantics v{r.engine_semantics}
            </p>
          </Card>
          <RunCharts runId={r.id} />
          <Card
            title={`Trades${q.data.tradeCount > q.data.trades.length ? ` (last ${q.data.trades.length} of ${q.data.tradeCount})` : ` (${q.data.tradeCount})`}`}
            flush
          >
            <SortableTable
              exportName={["trades", r.strategy_name, r.symbol, r.timeframe]}
              rows={q.data.trades}
              rowKey={(t) => String(t.seq)}
              initialSort={{ key: "seq", direction: "desc" }}
              columns={[
                { key: "seq", label: "#", align: "right", render: (t) => t.seq, sortValue: (t) => t.seq },
                {
                  key: "side",
                  label: "Side",
                  render: (t) => t.direction,
                  sortValue: (t) => t.direction,
                  firstDirection: "asc",
                },
                {
                  key: "entry",
                  label: "Entry",
                  render: (t) => date(t.entryTime, true),
                  sortValue: (t) => t.entryTime,
                },
                {
                  key: "exit",
                  label: "Exit",
                  render: (t) => date(t.exitTime, true),
                  sortValue: (t) => t.exitTime,
                },
                {
                  key: "in",
                  label: "Entry price",
                  align: "right",
                  render: (t) => money(t.entryPrice, 2),
                  sortValue: (t) => t.entryPrice,
                  exportValue: (t) => t.entryPrice,
                },
                {
                  key: "out",
                  label: "Exit price",
                  align: "right",
                  render: (t) => money(t.exitPrice, 2),
                  sortValue: (t) => t.exitPrice,
                  exportValue: (t) => t.exitPrice,
                },
                {
                  key: "pnl",
                  label: "P&L",
                  align: "right",
                  render: (t) => <span className={tone(t.pnl)}>{money(t.pnl, 2)}</span>,
                  sortValue: (t) => t.pnl,
                  exportValue: (t) => t.pnl,
                },
                {
                  key: "pct",
                  label: "P&L %",
                  align: "right",
                  render: (t) => <span className={tone(t.pnlPct)}>{pct(t.pnlPct * 100, 2)}</span>,
                  sortValue: (t) => t.pnlPct,
                  exportValue: (t) => t.pnlPct * 100,
                },
                {
                  key: "why",
                  label: "Exit reason",
                  render: (t) => t.exitReason.replace(/_/g, " "),
                  sortValue: (t) => t.exitReason,
                  firstDirection: "asc",
                },
              ]}
            />
          </Card>
          <Card>
            <CommentsPanel type="run" id={r.id} invalidate={inv} title="Review discussion" />
          </Card>
        </div>
        <div className="stack">
          {q.data.isOwner && <PublishRun run={r} />}
          <Card title="Ratings & reviews">
            <RatingPanel type="run" id={r.id} isOwner={q.data.isOwner} invalidate={inv} />
          </Card>
          <Card title="The rules">
            <SpecSummary spec={r.spec} />
          </Card>
          <Card title="Recent verifications" flush>
            {q.data.verifications.length === 0 ? (
              <ul className="versions">
                <li className="muted">Nobody has re-run this yet.</li>
              </ul>
            ) : (
              <PagedList
                className="versions"
                items={q.data.verifications}
                pageSize={10}
                render={(x, i) => (
                  <li key={i}>
                    <span className={x.matched ? "up" : "down"}>
                      {x.matched ? "✓ matched" : "✗ differed"}
                    </span>{" "}
                    <span className="muted small">
                      {x.display_name ?? "a guest"} · {timeAgo(x.created_at)}
                    </span>
                  </li>
                )}
              />
            )}
          </Card>
        </div>
      </div>
      <Disclaimer />
    </div>
  );
}
